import React, { useState, useEffect } from 'react';
import { Activity, Server, HardDrive, Cpu, Database, Video, RefreshCw } from 'lucide-react';
import apiClient from '@/services/api';

interface SystemMetrics {
  cache: {
    totalVideos: number;
    totalSizeGB: string;
    maxSizeGB: number;
    usagePercent: string;
  };
  disk: {
    total: string;
    used: string;
    free: string;
    usagePercent: string;
  };
  cpu: {
    loadAverage1min: string;
    cores: number;
  };
  memory: {
    totalGB: string;
    usedGB: string;
    usagePercent: string;
  };
  playout: {
    activeChannels: number;
    runningChannels: number;
  };
  transcode: {
    queuedJobs: number;
    activeJobs: number;
  };
}

const SystemMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchMetrics = async () => {
    try {
      setRefreshing(true);
      const response = await apiClient.get('/monitor/system');
      setMetrics(response.data.data);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const getUsageColor = (percent: string): string => {
    const value = parseFloat(percent);
    if (value >= 90) return 'text-red-600';
    if (value >= 75) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getUsageBgColor = (percent: string): string => {
    const value = parseFloat(percent);
    if (value >= 90) return 'bg-red-500';
    if (value >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Activity className="w-6 h-6 text-purple-600" />
          System Monitor
        </h3>
        <button
          onClick={fetchMetrics}
          disabled={refreshing}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          title="Refresh metrics"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Metrics Grid - Compact 2x3 layout */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        
        {/* Cache Usage */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-semibold text-gray-700">Cache</span>
          </div>
          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className={`text-2xl font-bold ${getUsageColor(metrics.cache.usagePercent)}`}>
                {metrics.cache.usagePercent}%
              </span>
              <span className="text-xs text-gray-500">{metrics.cache.totalVideos} videos</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${getUsageBgColor(metrics.cache.usagePercent)}`}
                style={{ width: `${Math.min(parseFloat(metrics.cache.usagePercent), 100)}%` }}
              ></div>
            </div>
          </div>
          <p className="text-xs text-gray-600">{metrics.cache.totalSizeGB} / {metrics.cache.maxSizeGB} GB</p>
        </div>

        {/* Disk Space */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <HardDrive className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-700">Disk</span>
          </div>
          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className={`text-2xl font-bold ${getUsageColor(metrics.disk.usagePercent.replace('%', ''))}`}>
                {metrics.disk.usagePercent}
              </span>
              <span className="text-xs text-gray-500">{metrics.disk.free} free</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${getUsageBgColor(metrics.disk.usagePercent.replace('%', ''))}`}
                style={{ width: metrics.disk.usagePercent }}
              ></div>
            </div>
          </div>
          <p className="text-xs text-gray-600">{metrics.disk.used} / {metrics.disk.total}</p>
        </div>

        {/* CPU Load */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Cpu className="w-4 h-4 text-orange-600" />
            <span className="text-sm font-semibold text-gray-700">CPU</span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-2xl font-bold text-gray-800">{metrics.cpu.loadAverage1min}</span>
            <span className="text-xs text-gray-500">load avg</span>
          </div>
          <p className="text-xs text-gray-600">{metrics.cpu.cores} cores</p>
        </div>

        {/* Memory Usage */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Server className="w-4 h-4 text-green-600" />
            <span className="text-sm font-semibold text-gray-700">Memory</span>
          </div>
          <div className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className={`text-2xl font-bold ${getUsageColor(metrics.memory.usagePercent)}`}>
                {metrics.memory.usagePercent}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${getUsageBgColor(metrics.memory.usagePercent)}`}
                style={{ width: `${metrics.memory.usagePercent}%` }}
              ></div>
            </div>
          </div>
          <p className="text-xs text-gray-600">{metrics.memory.usedGB} / {metrics.memory.totalGB} GB</p>
        </div>

        {/* Playout Status */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Video className="w-4 h-4 text-pink-600" />
            <span className="text-sm font-semibold text-gray-700">Channels</span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className={`text-2xl font-bold ${metrics.playout.runningChannels > 0 ? 'text-green-600' : 'text-gray-400'}`}>
              {metrics.playout.runningChannels}
            </span>
            <span className="text-xs text-gray-500">running</span>
          </div>
          <p className="text-xs text-gray-600">{metrics.playout.activeChannels} total</p>
        </div>

        {/* Transcode Queue */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-semibold text-gray-700">Transcode</span>
          </div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className={`text-2xl font-bold ${metrics.transcode.activeJobs > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
              {metrics.transcode.activeJobs}
            </span>
            <span className="text-xs text-gray-500">active</span>
          </div>
          <p className="text-xs text-gray-600">{metrics.transcode.queuedJobs} queued</p>
        </div>

      </div>

      {/* Auto-refresh indicator */}
      <p className="text-xs text-gray-500 mt-4 text-center">
        Auto-refreshes every 30 seconds
      </p>
    </div>
  );
};

export default SystemMonitor;

